import React, { ComponentProps, useEffect, useRef } from "react";
import ReactMarkdownPreview from "@uiw/react-markdown-preview/nohighlight";
import "@uiw/react-markdown-preview/markdown.css";
import "@uiw/react-md-editor/markdown-editor.css";
import rehypeSanitize from "rehype-sanitize";
import rehypeHighlight from "rehype-highlight";

export const MarkdownPreview = ({
    source,
    colorMode = "light",
    sanitize = true, // Important: `sanitize` removes the metadata required for the copy snippet to work
    onCopy,
    ...props
}: ComponentProps<typeof ReactMarkdownPreview> & {
    source: string;
    colorMode?: "light" | "dark";
    sanitize?: boolean;
    onCopy?: (text: string) => string;
}) => {
    const rehypePlugins = [sanitize ? rehypeSanitize : undefined, rehypeHighlight].filter(Boolean);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onCopy) {
            return;
        }

        // Handle manual text selection copy (Ctrl+C / Cmd+C)
        const handleCopyEvent = (event: ClipboardEvent) => {
            // Only intercept copy events within this component
            if (!containerRef.current?.contains(event.target as Node)) {
                return;
            }

            const selection = window.getSelection();
            if (selection && selection.toString().length > 0) {
                const selectedText = selection.toString();
                const transformedText = onCopy(selectedText);

                // If the transformed text differs from the original, update the clipboard
                if (transformedText !== selectedText) {
                    event.clipboardData?.setData("text/plain", transformedText);
                    event.preventDefault();
                }
            }
        };

        // Handle copy button clicks.
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Check if the click is within this component
            if (!containerRef.current?.contains(target)) {
                return;
            }

            // Check if the clicked element is a copy button or inside one
            // The @uiw/react-markdown-preview library uses various selectors for copy buttons
            const selectors = [
                ".copied",
                ".copy-button",
                'button[aria-label*="Copy"]',
                'button[class*="copied"]',
                "button[data-copied]",
                'button[title*="Copy"]'
            ];
            const copyButton =
                target.closest(selectors.join(",")) ||
                (target.tagName === "BUTTON" && target.closest("pre"));

            if (copyButton) {
                // Find the associated code block
                const codeBlock = copyButton.closest("pre")?.querySelector("code");

                if (codeBlock && codeBlock.textContent) {
                    const codeText = codeBlock.textContent;
                    const transformedText = onCopy(codeText);

                    // If the transformed text differs from the original, update the clipboard
                    if (transformedText !== codeText) {
                        event.preventDefault();
                        event.stopPropagation();
                        navigator.clipboard.writeText(transformedText);
                    }
                }
            }
        };

        document.addEventListener("copy", handleCopyEvent);
        // use capture to stop propagation to ReactMarkdownPreview's default behavior
        document.addEventListener("click", handleClick, true);

        return () => {
            document.removeEventListener("copy", handleCopyEvent);
            document.removeEventListener("click", handleClick, true);
        };
    }, [onCopy]);

    return (
        <div data-color-mode={colorMode} ref={containerRef}>
            <ReactMarkdownPreview
                source={source}
                rehypePlugins={rehypePlugins}
                {...props}
                className={`force-purple-links markdownStyling workflowsMarkdownStyling ${props.className || ""}`}
                style={{ fontFamily: "Inter", ...props.style }}
            />
        </div>
    );
};
